import React, { useRef, useState } from "react";
import Replay from "./Replay";
import styles from "./MatchReplay.module.scss";

// Watching a finished MATCH: the round player (Replay) plus what makes it a match:
// a round switcher in the control row (previous, a "Round N of M" dropdown, next)
// and, when a round plays out, the next one starting by itself after a beat, so a
// whole match can be watched hands-off. Used wherever a replay plays: the local
// and online results screens, a shared replay link, the admin viewer.

interface Props {
  match: any; // { matchConfig, matchState: { roundsData } }
  initialRound?: number;
  onExit: () => void;
  // Viewers that study a round (admin forensics) may OWN the round on screen: pass
  // `round` and follow onRoundChange (their own round tabs then drive the player
  // too). Left out, the player keeps the round itself, starting at initialRound.
  // They may also draw over the arena and get a handle to seek within the round.
  round?: number;
  onRoundChange?: (roundIndex: number) => void;
  arenaOverlay?: React.ReactNode;
  controlRef?: React.MutableRefObject<{ seekTo: (gameTime: number) => void } | null>;
  // A mark in the dropdown for rounds worth a look (e.g. ones with a death report).
  roundMark?: (roundIndex: number) => boolean;
  // The viewer's own seat(s), when known: their crash is raised on the timeline.
  youIds?: string[];
}

const NEXT_ROUND_HOLD_MS = 1500;

export default function MatchReplay({ match, initialRound = 0, onExit, round, onRoundChange, arenaOverlay, controlRef, roundMark, youIds }: Props) {
  const rounds: any[] = match.matchState.roundsData;
  const clampRound = (i: number) => Math.max(0, Math.min(rounds.length - 1, i));
  const [ownRound, setRoundIndexState] = useState(() => clampRound(initialRound));
  const roundIndex = round !== undefined ? clampRound(round) : ownRound;
  // The round on screen, readable from a callback of a player that is going away.
  const roundIndexRef = useRef(roundIndex);
  roundIndexRef.current = roundIndex;

  function goToRound(i: number) {
    if (i < 0 || i >= rounds.length || i === roundIndexRef.current) return;
    setRoundIndexState(i);
    if (onRoundChange) onRoundChange(i);
  }
  // Playback ran off the end of a round: hold on its last frame, then play the next.
  // (Scrubbing pauses, and a paused replay never runs off the end.)
  function onRoundPlayedOut() {
    const from = roundIndexRef.current;
    if (from >= rounds.length - 1) return;
    window.setTimeout(() => { if (roundIndexRef.current === from) goToRound(from + 1); }, NEXT_ROUND_HOLD_MS);
  }

  const switcher = rounds.length > 1 ? (
    <div className={styles.switcher}>
      <button onClick={() => goToRound(roundIndex - 1)} disabled={roundIndex === 0} title="Previous round" aria-label="Previous round">‹</button>
      <select value={roundIndex} onChange={(e) => goToRound(Number(e.target.value))} title="Round" aria-label="Round">
        {rounds.map((_: any, i: number) => (
          <option key={i} value={i}>Round {i + 1} of {rounds.length}{roundMark && roundMark(i) ? " •" : ""}</option>
        ))}
      </select>
      <button onClick={() => goToRound(roundIndex + 1)} disabled={roundIndex >= rounds.length - 1} title="Next round" aria-label="Next round">›</button>
    </div>
  ) : null;

  return (
    <Replay
      key={roundIndex}
      match={match}
      roundId={roundIndex}
      // Each round is its own player (keyed), and a player says "over" when it is torn
      // down as well as on Exit. Only Exit on the round still on screen leaves.
      onReplayOver={() => { if (roundIndexRef.current === roundIndex) onExit(); }}
      controlRef={controlRef}
      arenaOverlay={arenaOverlay}
      footerExtra={switcher}
      youIds={youIds}
      onRoundPlayedOut={onRoundPlayedOut}
    />
  );
}
